import type { ColumnDef } from "@tanstack/react-table";
import { PORTFOLIO_VALUATION_STATUS_OBJ, PRICE_SOURCE_OBJ } from "@signals/shared";
import { LockKeyhole } from "lucide-react";
import { formatCurrency, formatDisplayNumber, type SmartTableColumnMeta } from "../_common";
import type { MissingValuation, Position } from "./portfolio.hooks";
import {
  formatNumber,
  numericValue,
  rightDisplayCurrency,
  rightNumber,
  rightPercent,
} from "./portfolio-page-formatters";

function formatBlockingQuantity(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "—";
  }
  return formatDisplayNumber(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8,
  });
}

export function formatMarketPrice(value: number | undefined, currency: string): string {
  if (value === undefined || !Number.isFinite(value)) return "—";
  const absoluteValue = Math.abs(value);
  if (absoluteValue >= 0.01) return formatCurrency(value, currency);
  const sign = value < 0 ? "-" : "";
  const currencySymbol = formatCurrency(1, currency).replace("1", "");
  const price = absoluteValue < 0.00000001
    ? absoluteValue.toExponential(3)
    : formatDisplayNumber(absoluteValue, { maximumFractionDigits: 8 });
  return `${sign}${currencySymbol}${price}`;
}

export function aggregatePositionsBySymbol(positions: Position[]): Position[] {
  const groups = new Map<string, Position[]>();

  for (const position of positions) {
    const key = position.symbolId
      ? `symbol:${position.symbolId}`
      : `unresolved:${position.accountId ?? position.accountName ?? "unknown"}:${
          position.symbolTicker ?? position.symbolName ?? "unknown"
        }`;
    const group = groups.get(key) ?? [];
    group.push(position);
    groups.set(key, group);
  }

  return Array.from(groups.values(), (group): Position => {
    const first = group[0]!;
    const sum = (key: keyof Position): number =>
      group.reduce((total, position) => {
        const value = position[key];
        return total +
          (typeof value === "number" && Number.isFinite(value) ? value : 0);
      }, 0);
    const sumNullable = (key: keyof Position): number | null => {
      const values = group.map((position) => position[key]);
      if (
        values.some(
          (value) => typeof value !== "number" || !Number.isFinite(value),
        )
      ) {
        return null;
      }
      return values.reduce<number>((total, value) =>
        total + (typeof value === "number" && Number.isFinite(value) ? value : 0), 0);
    };

    const quantity = sum("quantity");
    const costBasis = sum("costBasis");
    const displayCostBasis = sum("displayCostBasis");
    const marketValue = sumNullable("marketValue");
    const marketValueUSD = sumNullable("marketValueUSD");
    const displayValue = sumNullable("displayValue");
    const unrealizedPnL = sumNullable("unrealizedPnL");
    const unrealizedPnLUSD = sumNullable("unrealizedPnLUSD");
    const displayUnrealizedPnL = sumNullable("displayUnrealizedPnL");
    const displayTotalReturn = sumNullable("displayTotalReturn");
    const totalReturn = sumNullable("totalReturn");
    const blockedQuantity = sumNullable("blockedQuantity");
    const availableQuantity = sumNullable("availableQuantity");
    const hasBlockedQuantity = group.some((position) => position.hasBlockedQuantity === true)
      ? true
      : group.some((position) =>
          position.hasBlockedQuantity === null || position.hasBlockedQuantity === undefined,
        )
        ? null
        : false;

    const accounts = new Map<
      string,
      { accountId: string | null; accountName: string; quantity: number }
    >();
    for (const position of group) {
      const accountName = position.accountName ?? "Unknown account";
      const accountId = position.accountId ?? null;
      const accountKey = accountId ?? accountName;
      const account = accounts.get(accountKey) ?? { accountId, accountName, quantity: 0 };
      account.quantity += position.quantity ?? 0;
      accounts.set(accountKey, account);
    }
    const accountBreakdown = Array.from(accounts.values());

    const missingValuationsByKey = new Map<string, MissingValuation>();
    for (const missing of group.flatMap(
      (position) => position.missingValuations ?? [],
    )) {
      missingValuationsByKey.set(`${missing.symbolId}:${missing.reason}`, missing);
    }
    const missingValuations = Array.from(missingValuationsByKey.values());
    const valuationStatus = group.some(
      (position) => position.valuationStatus === PORTFOLIO_VALUATION_STATUS_OBJ.INCOMPLETE,
    )
      ? PORTFOLIO_VALUATION_STATUS_OBJ.INCOMPLETE
      : PORTFOLIO_VALUATION_STATUS_OBJ.COMPLETE;
    const sellDates = group
      .map((position) => position.lastSellDate)
      .filter((value): value is string => typeof value === "string")
      .sort();
    const lastSellDate = sellDates[sellDates.length - 1] ?? null;

    return {
      ...first,
      accountId: null,
      accountName:
        accountBreakdown.length > 1
          ? `${accountBreakdown.length} accounts`
          : accountBreakdown[0]?.accountName ?? first.accountName ?? null,
      accountBreakdown,
      quantity,
      blockedQuantity,
      availableQuantity,
      hasBlockedQuantity,
      avgEntryPrice: quantity === 0 ? 0 : costBasis / quantity,
      displayAvgEntryPrice: quantity === 0 ? 0 : displayCostBasis / quantity,
      costBasis,
      costBasisUSD: sum("costBasisUSD"),
      displayCostBasis,
      marketValue,
      marketValueUSD,
      realizedPnL: sum("realizedPnL"),
      realizedPnLUSD: sum("realizedPnLUSD"),
      realizedPnLTradingUSD: sum("realizedPnLTradingUSD"),
      realizedPnLFxUSD: sum("realizedPnLFxUSD"),
      displayRealizedPnL: sum("displayRealizedPnL"),
      displayRealizedPnLTrading: sum("displayRealizedPnLTrading"),
      displayRealizedPnLFx: sum("displayRealizedPnLFx"),
      unrealizedPnL,
      unrealizedPnLUSD,
      displayUnrealizedPnL,
      dividends: sum("dividends"),
      dividendsUSD: sum("dividendsUSD"),
      displayDividends: sum("displayDividends"),
      totalCommissions: sum("totalCommissions"),
      totalCommissionsUSD: sum("totalCommissionsUSD"),
      displayTotalCommissions: sum("displayTotalCommissions"),
      displayValue,
      amountUSD: sumNullable("amountUSD"),
      roi:
        displayValue === null
          ? null
          : displayCostBasis === 0
            ? 0
            : ((displayValue - displayCostBasis) / displayCostBasis) * 100,
      dividendYield:
        valuationStatus === PORTFOLIO_VALUATION_STATUS_OBJ.INCOMPLETE
          ? null
          : displayCostBasis === 0
            ? 0
            : (sum("displayDividends") / displayCostBasis) * 100,
      totalReturn,
      displayTotalReturn,
      pendingQuantity: sum("pendingQuantity"),
      totalBoughtQty: sum("totalBoughtQty"),
      totalSoldQty: sum("totalSoldQty"),
      lastSellDate,
      valuationStatus,
      missingValuations,
    };
  });
}

function unknownBlockingStateExplanation(position: Position): string {
  return (position.pendingQuantity ?? 0) > 0
    ? "The conversion is pending; blocked and available quantities are unknown until it is credited."
    : "Blocking history is unavailable for this position on the selected date.";
}

export const positionBaseColumns = (
  displayCurrency: string,
  aggregateBySymbol = false,
): ColumnDef<Position>[] => [
  {
    accessorKey: "accountId",
    header: aggregateBySymbol ? "Accounts" : "Account",
    cell: ({ row }) => {
      const position = row.original;
      const accountBreakdown = position.accountBreakdown;
      if (!aggregateBySymbol || !accountBreakdown) {
        return position.accountName ?? "—";
      }
      const details = accountBreakdown
        .map(
          (account) =>
            `${account.accountName}: ${formatNumber(account.quantity)}`,
        )
        .join("; ");
      return <span title={details}>{details}</span>;
    },
    meta: {
      filterable: "client",
      filterType: "select-single",
      multiSelect: false,
    } satisfies SmartTableColumnMeta,
  },
  {
    accessorKey: "symbolTicker",
    header: "Ticker",
    cell: ({ row }) => {
      const position = row.original;
      const blockedQuantity = position.blockedQuantity;
      const availableQuantity = position.availableQuantity;
      const tooltip = blockedQuantity === null || blockedQuantity === undefined
        ? "Some quantity is blocked; the exact amount is unknown."
        : availableQuantity === null || availableQuantity === undefined
          ? `Blocked ${formatNumber(blockedQuantity)}; available quantity is unknown.`
          : `Blocked ${formatBlockingQuantity(blockedQuantity)} of ${formatBlockingQuantity(position.quantity)}`;
      return (
        <span className="inline-flex items-center gap-1">
          <span>{position.symbolTicker ?? "—"}</span>
          {position.hasBlockedQuantity === true && (
            <span
              role="img"
              aria-label={tooltip}
              title={tooltip}
              className="inline-flex text-orange-500"
            >
              <LockKeyhole className="h-4 w-4" aria-hidden="true" />
            </span>
          )}
        </span>
      );
    },
  },
  { accessorKey: "symbolName", header: "Name" },
  rightNumber("quantity", "Qty"),
  {
    accessorKey: "blockedQuantity",
    header: "Blocked",
    meta: { align: "right" },
    cell: ({ getValue, row }) => {
      const value = numericValue(getValue());
      if (value !== undefined) return formatBlockingQuantity(value);
      const explanation = unknownBlockingStateExplanation(row.original);
      return <span title={explanation} aria-label={`Unknown: ${explanation}`}>—</span>;
    },
  },
  {
    accessorKey: "availableQuantity",
    header: "Available",
    meta: { align: "right" },
    cell: ({ getValue, row }) => {
      const value = numericValue(getValue());
      if (value !== undefined) return formatBlockingQuantity(value);
      const explanation = unknownBlockingStateExplanation(row.original);
      return <span title={explanation} aria-label={`Unknown: ${explanation}`}>—</span>;
    },
  },
  rightDisplayCurrency("displayAvgEntryPrice", "Avg Entry", displayCurrency),
  rightDisplayCurrency("displayCostBasis", "Invested", displayCurrency),
  {
    accessorKey: "displayCurrentPrice",
    header: "Current",
    meta: { align: "right", customCell: true } satisfies SmartTableColumnMeta,
    cell: ({ getValue, row }) => {
      const value = numericValue(getValue());
      const source = row.original.marketPriceSource;
      const sourceLabel = source === PRICE_SOURCE_OBJ.MANUAL ? "Manual" : source === PRICE_SOURCE_OBJ.FMP ? "FMP" : source === PRICE_SOURCE_OBJ.MOEX ? "MOEX" : "Source unknown";
      const detail = row.original.marketPriceDate
        ? `Price date: ${row.original.marketPriceDate} · ${sourceLabel}${row.original.marketPriceIsStale ? " · old price" : ""}`
        : `Price date unavailable · ${sourceLabel}`;
      return (
        <span className="block text-right" title={detail}>
          <span className="block">{formatMarketPrice(value, displayCurrency)}</span>
          <span className="block text-xs text-muted-foreground">
            {row.original.marketPriceDate ?? "No price date"} · {sourceLabel}{row.original.marketPriceIsStale ? " · old price" : ""}
          </span>
        </span>
      );
    },
  },
  rightDisplayCurrency("displayValue", "Market Value", displayCurrency, true),
  rightDisplayCurrency(
    "displayUnrealizedPnL",
    "Unreal. P&L",
    displayCurrency,
    true,
  ),
  rightDisplayCurrency("displayRealizedPnL", "Real. P&L", displayCurrency),
  rightDisplayCurrency("displayRealizedPnLTrading", "Trading P&L", displayCurrency),
  rightDisplayCurrency("displayRealizedPnLFx", "FX P&L", displayCurrency),
  rightDisplayCurrency("displayDividends", "Div", displayCurrency),
  rightDisplayCurrency(
    "displayTotalReturn",
    "Total Return",
    displayCurrency,
    true,
  ),
  rightPercent("roi", "ROI", true),
  rightPercent("dividendYield", "Div Yield", true),
  { accessorKey: "currency", header: "Currency" },
];

/** Columns for the Closed tab — shows closed positions with sell metadata. */
export const closedPositionColumns = (displayCurrency: string): ColumnDef<Position>[] => [
  {
    accessorKey: "accountId",
    header: "Account",
    cell: ({ row }) => row.original.accountName ?? "—",
    meta: {
      filterable: "client",
      filterType: "select-single",
      multiSelect: false,
    } satisfies SmartTableColumnMeta,
  },
  { accessorKey: "symbolTicker", header: "Ticker" },
  { accessorKey: "symbolName", header: "Name" },
  { accessorKey: "lastSellDate", header: "Last Sell" },
  rightNumber("totalBoughtQty", "Bought Qty"),
  rightNumber("totalSoldQty", "Sold Qty"),
  rightDisplayCurrency("displayRealizedPnL", "Real. P&L", displayCurrency),
  rightDisplayCurrency("displayRealizedPnLTrading", "Trading P&L", displayCurrency),
  rightDisplayCurrency("displayRealizedPnLFx", "FX P&L", displayCurrency),
  rightDisplayCurrency("displayDividends", "Div", displayCurrency),
  rightDisplayCurrency("displayTotalCommissions", "Commissions", displayCurrency),
  rightDisplayCurrency("displayTotalReturn", "Total Return", displayCurrency),
  rightPercent("roi", "ROI"),
  { accessorKey: "currency", header: "Currency" },
];

